import React, { useState } from 'react';
import FarmManagement from './PropertyManagement/FarmManagement';
import MarketManagement from './PropertyManagement/MarketManagement';
import MineManagement from './PropertyManagement/MineManagement';
import FisheryManagement from './PropertyManagement/FisheryManagement';
import PortManagement from './PropertyManagement/PortManagement';
import OilRigManagement from './PropertyManagement/OilRigManagement';
import OilWellManagement from './PropertyManagement/OilWellManagement';
import LumberMillManagement from './PropertyManagement/LumberMillManagement';
import PowerPlantManagement from './PropertyManagement/PowerPlantManagement';
import FactoryManagement from './PropertyManagement/FactoryManagement';
import BankManagement from './PropertyManagement/BankManagement';
import ActiveLoansManagement from './PropertyManagement/ActiveLoansManagement';
import ConstructionManagement from './PropertyManagement/ConstructionManagement';
import UpgradeManagement from './PropertyManagement/UpgradeManagement';
import SellPropertyManagement from './PropertyManagement/SellPropertyManagement';

export default function PropertyManagement({
  selectedTile,
  userId,
  gameData,
  shippingRoutes,
  landRoutes,
  toggleShippingRoute,
  toggleLandRoute,
  isPortConnected,
  isLandConnected,
  getOwnedPorts,
  getOwnedFarms,
  getOwnedMarkets,
  getOwnedFisheries,
  getOwnedConstructionCompanies,
  getOwnedLumberMills,
  getOwnedFactories,
  getOwnedMines,
  getOwnedPowerPlants,
  getOwnedOilWells,
  getOwnedOilRigs,
  // Bank-related props
  depositToBank,
  withdrawFromBank,
  getBankDeposit,
  getTotalBankDeposits,
  getCurrentPlayer,
  setBankInterestRate,
  getBankInterestRate,
  getPlayerLoans,
  repayLoan,
  getOperatingCost,
  getBankInterestIncome,
  getTileIncome,
  // New props for upgrades
  upgradeTile,
  upgradeEquipment,
  getTileUpgradeLevel,
  getEquipmentUpgradeLevel,
  getUpgradeCost,
  getMaxRoutes,
  tradeAgreements = {},
  // New prop for selling
  sellProperty,
  economyEffects = [],
  frog = null,
  isMobile = false
}) {
  const [showShippingRoutes, setShowShippingRoutes] = useState(false);
  const [showLandRoutes, setShowLandRoutes] = useState(false);
  const [showBankManagement, setShowBankManagement] = useState(false);
  const [showActiveLoans, setShowActiveLoans] = useState(false); // New state
  const [showUpgrades, setShowUpgrades] = useState(false);
  const [showSellProperty, setShowSellProperty] = useState(false);
  const [routeLimit, setRouteLimit] = useState(3); // Default value

  // Get route limit from backend when component mounts
  React.useEffect(() => {
    if (gameData?.connection) {
      gameData.connection.invoke('GetRouteLimit');
      
      gameData.connection.on('RouteLimitInfo', (data) => {
        setRouteLimit(data.maxRoutesPerTile);
      });

      return () => {
        gameData.connection.off('RouteLimitInfo');
      };
    }
  }, [gameData?.connection]);

  if (!selectedTile || !selectedTile.owner || selectedTile.owner.owner !== userId) {
    return null;
  }

  const getPropertyManagementOptions = (tileType) => {
    const managementOptions = {
      3: { name: 'Farm Management', options: ['Connected Routes', 'Upgrades', 'Sell Property'] },
      4: { name: 'Market Management', options: ['Connected Routes', 'Upgrades', 'Sell Property'] },
      5: { name: 'Mine Management', options: ['Connected Routes', 'Upgrades', 'Sell Property'] },
      6: { name: 'Fishery Management', options: ['Connected Routes', 'Upgrades', 'Sell Property'] },
      7: { name: 'Port Management', options: ['Shipping Routes', 'Upgrades', 'Sell Property'] },
      8: { name: 'Oil Rig Management', options: ['Shipping Routes', 'Upgrades', 'Sell Property'] },
      9: { name: 'Oil Well Management', options: ['Connected Routes', 'Upgrades', 'Sell Property'] },
      10: { name: 'Lumber Mill Management', options: ['Connected Routes', 'Upgrades', 'Sell Property'] },
      11: { name: 'Power Plant Management', options: ['Connected Routes', 'Upgrades', 'Sell Property'] },
      12: { name: 'Factory Management', options: ['Connected Routes', 'Upgrades', 'Sell Property'] },
      13: { name: 'Bank Management', options: ['Bank Management', 'Active Loans', 'Sell Property'] },
      14: { name: 'Construction Management', options: ['Connected Routes', 'Upgrades', 'Sell Property'] }
    };
    
    return managementOptions[tileType] || { name: 'Property Management', options: [] };
  };

  const handleOptionClick = (option) => {
    if (option === 'Shipping Routes' && (selectedTile.type === 7 || selectedTile.type === 8)) {
      setShowShippingRoutes(!showShippingRoutes);
    } else if (option === 'Connected Routes' && (selectedTile.type === 3 || selectedTile.type === 4 || selectedTile.type === 5 || selectedTile.type === 6 || selectedTile.type === 9 || selectedTile.type === 10 || selectedTile.type === 11 || selectedTile.type === 12 || selectedTile.type === 14)) {
      setShowLandRoutes(!showLandRoutes);
    } else if (option === 'Bank Management' && selectedTile.type === 13) {
      setShowBankManagement(!showBankManagement);
    } else if (option === 'Active Loans' && selectedTile.type === 13) {
      setShowActiveLoans(!showActiveLoans);
    } else if (option === 'Upgrades') {
      setShowUpgrades(!showUpgrades);
    } else if (option === 'Sell Property') {
      setShowSellProperty(!showSellProperty);
    } else {
      console.log(`Managing ${option} for ${getPropertyManagementOptions(selectedTile.type).name}`);
    }
  };

  const managementOptions = getPropertyManagementOptions(selectedTile.type);

  // Get trade partner's properties for cross-player connections
  const getTradePartner = () => {
    const agreements = Object.values(tradeAgreements);
    const activeAgreement = agreements.find(ta => 
      (ta.requesterId === userId || ta.targetId === userId) && ta.status === 1 // Active
    );
    
    if (!activeAgreement) return null;
    
    return activeAgreement.requesterId === userId ? activeAgreement.targetId : activeAgreement.requesterId;
  };

  const getTradePartnerProperties = (tileType) => {
    const tradePartnerId = getTradePartner();
    if (!tradePartnerId || !gameData.tileOwners) return [];
    
    const properties = [];
    Object.entries(gameData.tileOwners).forEach(([tileKey, ownership]) => {
      if (ownership.owner === tradePartnerId) {
        const [x, y] = tileKey.split(',').map(Number);
        const tileIndex = y * gameData.worldData.width + x;
        const tileTileType = gameData.worldData.tiles[tileIndex];
        
        if (tileTileType === tileType) {
          properties.push({
            key: tileKey,
            x,
            y,
            name: `${getTileTypeName(tileType)} (${x},${y})`
          });
        }
      }
    });
    
    return properties;
  };

  const getTileTypeName = (tileType) => {
    const names = {
      3: 'Farm', 4: 'Market', 5: 'Mine', 6: 'Fishery', 7: 'Port',
      8: 'Oil Rig', 9: 'Oil Well', 10: 'Lumber Mill', 11: 'Power Plant',
      12: 'Factory', 13: 'Bank', 14: 'Construction Company'
    };
    return names[tileType] || 'Property';
  };

  const getTradePartnerMarkets = () => getTradePartnerProperties(4);
  const getTradePartnerFarms = () => getTradePartnerProperties(3);
  const getTradePartnerFisheries = () => getTradePartnerProperties(6);
  const getTradePartnerMines = () => getTradePartnerProperties(5);
  const getTradePartnerFactories = () => getTradePartnerProperties(12);
  const getTradePartnerPowerPlants = () => getTradePartnerProperties(11);
  const getTradePartnerOilWells = () => getTradePartnerProperties(9);
  const getTradePartnerLumberMills = () => getTradePartnerProperties(10);
  const getTradePartnerConstructionCompanies = () => getTradePartnerProperties(14);
  const getTradePartnerPorts = () => getTradePartnerProperties(7);
  const getTradePartnerOilRigs = () => getTradePartnerProperties(8);

  // Helper to check if a bank can be sold (no deposits, no active loans)
  const checkCanSellBank = (bankTileKey) => {
    // Check if there are any deposits in this bank from any player
    // We'll use getBankDeposit for the current player, but for a full check, you may need to pass all deposits as a prop
    // For now, assume only the owner's deposits matter (as in your frontend logic)
    const totalDeposits = getBankDeposit(bankTileKey);
    if (totalDeposits > 0) return false;

    // Check if there are any active loans from this bank
    const loans = getPlayerLoans ? getPlayerLoans() : [];
    const hasActiveLoans = loans.some(loan => loan.bankTileKey === bankTileKey && loan.isActive);
    return !hasActiveLoans;
  };

  // Helper to get all loans for a specific bank
  const getBankLoans = (bankTileKey) => {
    if (!getPlayerLoans) return [];
    
    // Get all active loans and filter for this specific bank
    const allLoans = getPlayerLoans();
    return allLoans.filter(loan => 
      loan.bankTileKey === bankTileKey && 
      loan.isActive &&
      loan.borrowerId !== userId // Exclude current user's own loans
    );
  };

  // Helper function to get economy effect for current tile
  const getEconomyEffect = () => {
    if (!economyEffects || economyEffects.length === 0) return null;
    return economyEffects.find(effect => effect.tileType === selectedTile.type);
  };

  // Helper function to get equipment upgrade level
  const getEquipmentUpgradeLevelLocal = (tileKey) => {
    return getEquipmentUpgradeLevel ? getEquipmentUpgradeLevel(tileKey) : 0;
  };

  // Helper function to get equipment multiplier
  const getEquipmentMultiplierLocal = (equipmentLevel) => {
    const multipliers = { 0: 1.0, 1: 1.25, 2: 1.5, 3: 2.0 };
    return multipliers[equipmentLevel] || 1.0;
  };

  // Helper function to upgrade equipment
  const upgradeEquipmentLocal = async (tileKey) => {
    if (upgradeEquipment) {
      return upgradeEquipment(tileKey);
    }
  };

  // Helper function to calculate adjusted income with equipment multiplier
  const getAdjustedIncome = () => {
    const baseIncome = selectedTile.type === 13 
      ? getBankInterestIncome(selectedTile.key) 
      : getTileIncome(selectedTile.key, selectedTile.type);
    
    // Apply equipment multiplier
    const equipmentLevel = getEquipmentUpgradeLevelLocal(selectedTile.key);
    const equipmentMultiplier = getEquipmentMultiplierLocal(equipmentLevel);
    let incomeWithEquipment = baseIncome * equipmentMultiplier;
    
    // Apply economy effect
    const effect = getEconomyEffect();
    if (effect) {
      const economyMultiplier = 1 + (effect.effectPercentage / 100);
      incomeWithEquipment *= economyMultiplier;
    }
    
    return incomeWithEquipment;
  };
  // Helper function to check if frog is on current tile
  const isFrogOnTile = () => {
    if (!frog || !selectedTile) return false;
    return frog.currentTileKey === selectedTile.key && frog.state === 2; // StayingOnIndustry = 2
  };

  // Helper function to get the current frog multiplier based on time
  const getFrogMultiplier = () => {
    if (!gameData || (!gameData.gameActualStartTime && !gameData.gameStartTime)) return 2;
    
    const startTime = new Date(gameData.gameActualStartTime || gameData.gameStartTime);
    const elapsedMinutes = (new Date() - startTime) / 60000;
    
    if (elapsedMinutes >= 30) return 5;
    if (elapsedMinutes >= 20) return 4;
    if (elapsedMinutes >= 10) return 3;
    return 2;
  };

  // Helper function to get operating cost with equipment multiplier
  const getOperatingCostWithEquipmentAndFrog = (tileType) => {
    let baseCost = getOperatingCost(tileType);
    
    // Apply equipment multiplier
    const equipmentLevel = getEquipmentUpgradeLevelLocal(selectedTile.key);
    const equipmentMultiplier = getEquipmentMultiplierLocal(equipmentLevel);
    baseCost *= equipmentMultiplier;
    
    // Apply frog effect
    if (isFrogOnTile()) {
      baseCost *= getFrogMultiplier();
    }
    
    return baseCost;
  };

  return (
    <div style={{
      position: 'absolute',
      top: isMobile ? (selectedTile.owner ? '150px' : '130px') : (selectedTile.owner ? '180px' : '160px'),
      right: '10px',
      background: 'rgba(76, 175, 80, 0.9)',
      color: 'white',
      padding: '15px',
      borderRadius: '8px',
      fontFamily: 'Arial, sans-serif',
      width: '250px',
      maxWidth: '250px',
      maxHeight: 'calc(100vh - 200px)',
      overflowY: 'auto'
    }}>
      <h3 style={{ margin: '0 0 10px 0', textAlign: 'center', color: '#E8F5E8', wordWrap: 'break-word' }}>
        🏢 {managementOptions.name}
      </h3>
      
      <div style={{ fontSize: '0.9em', marginBottom: '15px', textAlign: 'center', color: '#C8E6C9', wordWrap: 'break-word' }}>
        Property Status: Active
      </div>
      
      <div style={{ marginBottom: '15px' }}>
        <h4 style={{ margin: '0 0 8px 0', fontSize: '0.9em', color: '#E8F5E8', wordWrap: 'break-word' }}>Management Options:</h4>
        {managementOptions.options.length > 0 ? (
          managementOptions.options.map((option, index) => (
            <button
              key={index}
              style={{
                display: 'block',
                width: '100%',
                padding: '6px 10px',
                marginBottom: '5px',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.8em',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
              onClick={() => handleOptionClick(option)}
            >
              {option}
            </button>
          ))
        ) : (
          <div style={{ 
            fontSize: '0.75em', 
            color: 'rgba(255, 255, 255, 0.7)',
            textAlign: 'center',
            fontStyle: 'italic',
            padding: '10px',
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '4px',
            wordWrap: 'break-word'
          }}>
          </div>
        )}
      </div>
      
      {/* Render the appropriate management component based on tile type */}
      {selectedTile.type === 3 && showLandRoutes && (
        <FarmManagement
          selectedTile={selectedTile}
          userId={userId}
          landRoutes={landRoutes}
          toggleLandRoute={toggleLandRoute}
          isLandConnected={isLandConnected}
          getOwnedMarkets={getOwnedMarkets}
          routeLimit={routeLimit}
          getTileUpgradeLevel={getTileUpgradeLevel}
          getMaxRoutes={getMaxRoutes}
          getTradePartnerMarkets={getTradePartnerMarkets}
          getEquipmentUpgradeLevel={getEquipmentUpgradeLevelLocal}
          getEquipmentMultiplier={getEquipmentMultiplierLocal}
        />
      )}

      {selectedTile.type === 4 && showLandRoutes && (
        <MarketManagement
          selectedTile={selectedTile}
          userId={userId}
          landRoutes={landRoutes}
          toggleLandRoute={toggleLandRoute}
          isLandConnected={isLandConnected}
          getOwnedFarms={getOwnedFarms}
          getOwnedFisheries={getOwnedFisheries}
          routeLimit={routeLimit}
          getTileUpgradeLevel={getTileUpgradeLevel}
          getMaxRoutes={getMaxRoutes}
          getTradePartnerFarms={getTradePartnerFarms}
          getTradePartnerFisheries={getTradePartnerFisheries}
          getEquipmentUpgradeLevel={getEquipmentUpgradeLevelLocal}
          getEquipmentMultiplier={getEquipmentMultiplierLocal}
        />
      )}

      {selectedTile.type === 5 && showLandRoutes && (
        <MineManagement
          selectedTile={selectedTile}
          userId={userId}
          landRoutes={landRoutes}
          toggleLandRoute={toggleLandRoute}
          isLandConnected={isLandConnected}
          getOwnedFactories={getOwnedFactories}
          getOwnedPowerPlants={getOwnedPowerPlants}
          routeLimit={routeLimit}
          getTileUpgradeLevel={getTileUpgradeLevel}
          getMaxRoutes={getMaxRoutes}
          getTradePartnerFactories={getTradePartnerFactories}
          getTradePartnerPowerPlants={getTradePartnerPowerPlants}
          getEquipmentUpgradeLevel={getEquipmentUpgradeLevelLocal}
          getEquipmentMultiplier={getEquipmentMultiplierLocal}
        />
      )}

      {selectedTile.type === 6 && showLandRoutes && (
        <FisheryManagement
          selectedTile={selectedTile}
          userId={userId}
          landRoutes={landRoutes}
          toggleLandRoute={toggleLandRoute}
          isLandConnected={isLandConnected}
          getOwnedMarkets={getOwnedMarkets}
          routeLimit={routeLimit}
          getTileUpgradeLevel={getTileUpgradeLevel}
          getMaxRoutes={getMaxRoutes}
          getTradePartnerMarkets={getTradePartnerMarkets}
          getEquipmentUpgradeLevel={getEquipmentUpgradeLevelLocal}
          getEquipmentMultiplier={getEquipmentMultiplierLocal}
        />
      )}

      {selectedTile.type === 7 && showShippingRoutes && (
        <PortManagement
          selectedTile={selectedTile}
          userId={userId}
          shippingRoutes={shippingRoutes}
          toggleShippingRoute={toggleShippingRoute}
          isPortConnected={isPortConnected}
          getOwnedPorts={getOwnedPorts}
          getOwnedOilRigs={getOwnedOilRigs}
          routeLimit={routeLimit}
          getTileUpgradeLevel={getTileUpgradeLevel}
          getMaxRoutes={getMaxRoutes}
          getTradePartnerPorts={getTradePartnerPorts}
          getTradePartnerOilRigs={getTradePartnerOilRigs}
          getEquipmentUpgradeLevel={getEquipmentUpgradeLevelLocal}
          getEquipmentMultiplier={getEquipmentMultiplierLocal}
        />
      )}

      {selectedTile.type === 8 && showShippingRoutes && (
        <OilRigManagement
          selectedTile={selectedTile}
          userId={userId}
          shippingRoutes={shippingRoutes}
          toggleShippingRoute={toggleShippingRoute}
          isPortConnected={isPortConnected}
          getOwnedPorts={getOwnedPorts}
          routeLimit={routeLimit}
          getTileUpgradeLevel={getTileUpgradeLevel}
          getMaxRoutes={getMaxRoutes}
          getTradePartnerPorts={getTradePartnerPorts}
          getEquipmentUpgradeLevel={getEquipmentUpgradeLevelLocal}
          getEquipmentMultiplier={getEquipmentMultiplierLocal}
        />
      )}

      {selectedTile.type === 9 && showLandRoutes && (
        <OilWellManagement
          selectedTile={selectedTile}
          userId={userId}
          landRoutes={landRoutes}
          toggleLandRoute={toggleLandRoute}
          isLandConnected={isLandConnected}
          getOwnedFactories={getOwnedFactories}
          routeLimit={routeLimit}
          getTileUpgradeLevel={getTileUpgradeLevel}
          getMaxRoutes={getMaxRoutes}
          getTradePartnerFactories={getTradePartnerFactories}
          getEquipmentUpgradeLevel={getEquipmentUpgradeLevelLocal}
          getEquipmentMultiplier={getEquipmentMultiplierLocal}
        />
      )}

      {selectedTile.type === 10 && showLandRoutes && (
        <LumberMillManagement
          selectedTile={selectedTile}
          userId={userId}
          landRoutes={landRoutes}
          toggleLandRoute={toggleLandRoute}
          isLandConnected={isLandConnected}
          getOwnedConstructionCompanies={getOwnedConstructionCompanies}
          routeLimit={routeLimit}
          getTileUpgradeLevel={getTileUpgradeLevel}
          getMaxRoutes={getMaxRoutes}
          getTradePartnerConstructionCompanies={getTradePartnerConstructionCompanies}
          getEquipmentUpgradeLevel={getEquipmentUpgradeLevelLocal}
          getEquipmentMultiplier={getEquipmentMultiplierLocal}
        />
      )}

      {selectedTile.type === 11 && showLandRoutes && (
        <PowerPlantManagement
          selectedTile={selectedTile}
          userId={userId}
          landRoutes={landRoutes}
          toggleLandRoute={toggleLandRoute}
          isLandConnected={isLandConnected}
          getOwnedMines={getOwnedMines}
          routeLimit={routeLimit}
          getTileUpgradeLevel={getTileUpgradeLevel}
          getMaxRoutes={getMaxRoutes}
          getTradePartnerMines={getTradePartnerMines}
          getEquipmentUpgradeLevel={getEquipmentUpgradeLevelLocal}
          getEquipmentMultiplier={getEquipmentMultiplierLocal}
        />
      )}

      {selectedTile.type === 12 && showLandRoutes && (
        <FactoryManagement
          selectedTile={selectedTile}
          userId={userId}
          landRoutes={landRoutes}
          toggleLandRoute={toggleLandRoute}
          isLandConnected={isLandConnected}
          getOwnedMines={getOwnedMines}
          getOwnedOilWells={getOwnedOilWells}
          routeLimit={routeLimit}
          getTileUpgradeLevel={getTileUpgradeLevel}
          getMaxRoutes={getMaxRoutes}
          getTradePartnerMines={getTradePartnerMines}
          getTradePartnerOilWells={getTradePartnerOilWells}
          getEquipmentUpgradeLevel={getEquipmentUpgradeLevelLocal}
          getEquipmentMultiplier={getEquipmentMultiplierLocal}
        />
      )}

      {selectedTile.type === 13 && showBankManagement && (
        <BankManagement
          selectedTile={selectedTile}
          userId={userId}
          depositToBank={depositToBank}
          withdrawFromBank={withdrawFromBank}
          getBankDeposit={getBankDeposit}
          getTotalBankDeposits={getTotalBankDeposits}
          getCurrentPlayer={getCurrentPlayer}
          setBankInterestRate={setBankInterestRate}
          getBankInterestRate={getBankInterestRate}
          getPlayerLoans={getPlayerLoans}
          repayLoan={repayLoan}
          onSellProperty={sellProperty}
          getTileUpgradeLevel={getTileUpgradeLevel}
          getUpgradeCost={getUpgradeCost}
          checkCanSellBank={checkCanSellBank}
          gameData={gameData}
        />
      )}

      {selectedTile.type === 13 && showActiveLoans && (
        <ActiveLoansManagement
          selectedTile={selectedTile}
          userId={userId}
          getBankLoans={getBankLoans}
        />
      )}

      {selectedTile.type === 14 && showLandRoutes && (
        <ConstructionManagement
          selectedTile={selectedTile}
          userId={userId}
          landRoutes={landRoutes}
          toggleLandRoute={toggleLandRoute}
          isLandConnected={isLandConnected}
          getOwnedLumberMills={getOwnedLumberMills}
          routeLimit={routeLimit}
          getTileUpgradeLevel={getTileUpgradeLevel}
          getMaxRoutes={getMaxRoutes}
          getTradePartnerLumberMills={getTradePartnerLumberMills}
          getEquipmentUpgradeLevel={getEquipmentUpgradeLevelLocal}
          getEquipmentMultiplier={getEquipmentMultiplierLocal}
        />
      )}

      {/* Add the Upgrades component for all industry tiles */}
      {showUpgrades && selectedTile.type >= 3 && selectedTile.type <= 14 && (
        <UpgradeManagement
          selectedTile={selectedTile}
          userId={userId}
          getCurrentPlayer={getCurrentPlayer}
          upgradeTile={upgradeTile}
          upgradeEquipment={upgradeEquipmentLocal}
          getTileUpgradeLevel={getTileUpgradeLevel}
          getEquipmentUpgradeLevel={getEquipmentUpgradeLevelLocal}
          getUpgradeCost={getUpgradeCost}
          getMaxRoutes={getMaxRoutes}
          getEquipmentMultiplier={getEquipmentMultiplierLocal}
        />
      )}

      {/* Add the Sell Property component for all industry tiles (including banks) */}
      {showSellProperty && selectedTile.type >= 3 && selectedTile.type <= 14 && (
        <SellPropertyManagement
          selectedTile={selectedTile}
          userId={userId}
          onSellProperty={sellProperty}
          getTileUpgradeLevel={getTileUpgradeLevel}
          getEquipmentUpgradeLevel={getEquipmentUpgradeLevelLocal}
          getUpgradeCost={getUpgradeCost}
          checkCanSellBank={checkCanSellBank}
        />
      )}
      
      <div style={{ 
        borderTop: '1px solid rgba(255, 255, 255, 0.3)', 
        paddingTop: '10px',
        fontSize: '0.8em'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
          <span>Income:</span>
          <span style={{ color: '#90EE90' }}>
            {(() => {
              const effect = getEconomyEffect();
              const equipmentLevel = getEquipmentUpgradeLevelLocal(selectedTile.key);
              const equipmentMultiplier = getEquipmentMultiplierLocal(equipmentLevel);
              
              const baseIncome = selectedTile.type === 13 
                ? getBankInterestIncome(selectedTile.key) 
                : getTileIncome(selectedTile.key, selectedTile.type);
              const adjustedIncome = getAdjustedIncome();
              
              // Show breakdown if either equipment or economy effect is active
              if ((equipmentLevel > 0 || effect) && baseIncome > 0) {
                return (
                  <span>
                    <span style={{ 
                      textDecoration: 'line-through', 
                      opacity: 0.6,
                      marginRight: '4px'
                    }}>
                      ${baseIncome?.toLocaleString() || '0'}
                    </span>
                    <span style={{ 
                      color: '#90EE90',
                      fontWeight: 'bold'
                    }}>
                      ${adjustedIncome?.toLocaleString() || '0'}/min
                    </span>
                    {equipmentLevel > 0 && (
                      <span style={{ 
                        fontSize: '0.7em', 
                        marginLeft: '4px',
                        color: '#FFD700'
                      }}>
                        (⚙️×{equipmentMultiplier.toFixed(2)})
                      </span>
                    )}
                    {effect && (
                      <span style={{ 
                        fontSize: '0.7em', 
                        marginLeft: '2px',
                        color: effect.effectPercentage > 0 ? '#90EE90' : '#FF6B6B'
                      }}>
                        ({effect.effectPercentage > 0 ? '+' : ''}{effect.effectPercentage}%)
                      </span>
                    )}
                  </span>
                );
              }
              
              return `$${baseIncome?.toLocaleString() || '0'}/min`;
            })()}
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
          <span>Operating Costs:</span>
          <span style={{ color: '#FFB74D' }}>
            {(() => {
              const equipmentLevel = getEquipmentUpgradeLevelLocal(selectedTile.key);
              const equipmentMultiplier = getEquipmentMultiplierLocal(equipmentLevel);
              
              const baseCost = getOperatingCost(selectedTile.type);
              const actualCost = getOperatingCostWithEquipmentAndFrog(selectedTile.type);
              
              // Show breakdown if equipment level > 0 or frog is present
              if ((equipmentLevel > 0 || isFrogOnTile()) && baseCost > 0) {
                return (
                  <span>
                    <span style={{ 
                      textDecoration: 'line-through', 
                      opacity: 0.6,
                      marginRight: '4px'
                    }}>
                      ${baseCost?.toLocaleString() || '0'}
                    </span>
                    <span style={{ 
                      color: isFrogOnTile() ? '#FF6B6B' : '#FFB74D', 
                      fontWeight: 'bold' 
                    }}>
                      ${actualCost?.toLocaleString() || '0'}/min
                    </span>
                    {equipmentLevel > 0 && (
                      <span style={{ 
                        fontSize: '0.7em', 
                        marginLeft: '4px',
                        color: '#FFD700'
                      }}>
                        (⚙️×{equipmentMultiplier.toFixed(2)})
                      </span>
                    )}
                    {isFrogOnTile() && (
                      <span style={{ 
                        fontSize: '0.7em', 
                        marginLeft: '2px',
                        color: '#8FBC8F'
                      }}>
                        (🐸×2)
                      </span>
                    )}
                  </span>
                );
              }
              
              return `$${baseCost?.toLocaleString() || '0'}/min`;
            })()}
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
          <span>Max Routes:</span>
          <span style={{ color: '#C8E6C9' }}>
            {getMaxRoutes ? getMaxRoutes(getTileUpgradeLevel ? getTileUpgradeLevel(selectedTile.key) : 0) : routeLimit}
            {getTileUpgradeLevel && getTileUpgradeLevel(selectedTile.key) > 0 && (
              <span style={{ color: '#90EE90', fontSize: '0.9em' }}> (Upgraded)</span>
            )}
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
          <span>Net Profit:</span>
          <span style={{ 
            color: (() => {
              const income = getAdjustedIncome();
              const operatingCost = getOperatingCostWithEquipmentAndFrog(selectedTile.type);
              return income > operatingCost ? '#90EE90' : '#FF6B6B';
            })()
          }}>
            {(() => {
              const income = getAdjustedIncome();
              const operatingCost = getOperatingCostWithEquipmentAndFrog(selectedTile.type);
              const netProfit = income - operatingCost;
              return `${netProfit >= 0 ? '$' : '-$'}${Math.abs(netProfit)?.toLocaleString() || '0'}/min`;
            })()
          }
          </span>
        </div>
      </div>
      
      <div style={{ 
        marginTop: '10px',
        textAlign: 'center',
        fontSize: '0.7em',
        color: 'rgba(255, 255, 255, 0.7)',
        wordWrap: 'break-word'
      }}>
      </div>
    </div>
  );
}